import React, {useRef, useEffect} from 'react';
import cn from 'classnames';
import Icon from 'HTKit/Icon';
import PropTypes from 'prop-types';
import {useDropzone} from 'react-dropzone';
import useCloudinaryHook from 'src/components/Cloudinary/hooks/useCloudinary'
import {
  FILE_UPLOAD_VALIDATIONS,
  RESOURCE_TYPE_IMAGE,
} from 'src/components/Cloudinary/cloudinary.constants';
import { ThumbnailDisplay } from 'Elements/ThumbnailDisplay';
import { tranformDragDropToResponseErrors } from 'src/components/Cloudinary/cloudinary.utils';

import styles from './styles.scss';

/**
 * This Drag Drop is integrated with Cloudinary. We need to use this within a same container.
 * Since we use redux to namespace usages, we "could" jump children in the display. Gladly, this display
 * is self contained. We do pipe up to redux to that any outside needs (forms) can get at the assets for
 * submission or whatever you may need.
 *
 * @param namespace - string - namespace for redux
 * @param size - number - size of the thumbnail
 * @param onChange - function - callback for when the assets change.
 *                   In our case here, onChangeAnswer for QA.
 * @constructor
 */

const MediaUploadWithThumbnailDisplayQA = ({namespace, size, onChange, seedValue, error}) => {
  /* Hooks */
  const {
    uploadFiles,
    uploadedSuccessFiles,
    handleDeleteFile,
    totalSuccessFilesCount,
    handleTransformImage,
    handleUploadErrors,
  } = useCloudinaryHook({namespace, height: size, width: size, folder: "private/quotes"});
  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject
  } = useDropzone({
    accept: {'image/*': FILE_UPLOAD_VALIDATIONS.ALLOWED_QA_DROPZONE_ACCEPT()},
    maxSize: FILE_UPLOAD_VALIDATIONS.MAX_FILE_SIZE,
    multiple: true,
    onDropAccepted: uploadFiles,
    onDropRejected: (file) => {
      handleUploadErrors(tranformDragDropToResponseErrors(file))
    },
  });

  /* Styles */
  const dropzoneContainer = cn(styles.simpleUploadContainer,{
    [styles.dropzoneFocusedAccept]: isDragAccept,
    [styles.dropzoneFocusedReject]: isDragReject,
    [styles.requiredError]: Boolean(error),
  })

  /* Constants */
  const fileInputRef = useRef(null);
  const hasFiles = uploadedSuccessFiles && uploadedSuccessFiles.length > 0;
  /* Normalize Data for Display: Need for component Thumbnail Display */
  const files = (uploadedSuccessFiles ?? []).map((file) => {
    return {
      name: file.original_filename,
      fileType: file.format?.toUpperCase(),
      showDownloadButton: false,
      showDeleteButton: true,
      suppressNameplate: true,
      transformedFile: file.resource_type === RESOURCE_TYPE_IMAGE && file.format !== 'pdf' ? file.transformed_image : '',
      onDownloadClick: () => {
        window.open(file.url, '_self');
      },
      onDeleteClick: () => {
        handleDeleteFile(file);
      },
      url: file.url,
    };
  });

  /* Methods */
  const onHandleOpenInputWindow = () => fileInputRef.current?.click();

  /*
     Whenever the total success files changes, update the on answer change method.
  */
  useEffect(() => {
      if (uploadedSuccessFiles) {
        onChange(uploadedSuccessFiles);
      }
  }, [uploadedSuccessFiles])

  /**
   * Load the preseeded data on mount. After that, the local state handles the media.
   */
  useEffect(() => {
    if (seedValue && seedValue.length > 0) {
      handleTransformImage(seedValue);
    }
  }, [seedValue]);

  return (
    <div>
      {
        /* Image Drop Display: Shows when no assets exist or one is in transit */
        (!hasFiles && !totalSuccessFilesCount) && (
          <>
          <div {...getRootProps()} className={dropzoneContainer}>
            <div className="text-weight-med flex flexDirectionColumn justifyContentCenter alignItemsCenter">
              <div><Icon name="add-image" /></div>
              <div className="block caption n300">max {FILE_UPLOAD_VALIDATIONS.MAX_FILE_SIZE_DISPLAY_STRING} per attachment</div>
            </div>
            <input {...getInputProps()} />
          </div>
          {Boolean(error) && (
            <div className="caption alertRed500">{error}</div>
          )}
          </>
        )
      }
      <div className="paddingY-small1">
        <ThumbnailDisplay className={styles.thumbailsContainerEv} parentPadding={16} files={files} thumbnailClassName={styles.imageContainerThumb} totalSuccessFilesCount={totalSuccessFilesCount} />
      </div>
      {
        /* Show text upload when users has uploads */
        hasFiles && (
          <>
            <p className="text-link p1 underline whiteSpaceNoWrap paddingBottom-small1" onClick={onHandleOpenInputWindow}>Add Images</p>
            <input
              type="file"
              ref={fileInputRef}
              style={{display: 'none'}}
              accept={FILE_UPLOAD_VALIDATIONS.ALLOWED_QA_DROPZONE_ACCEPT().join(',')}
              multiple
              onChange={e => {
                // eslint-disable-next-line no-shadow
                const {files} = e.target;
                uploadFiles(files);
              }}
            />
          </>
          )
      }
    </div>
  )
}

MediaUploadWithThumbnailDisplayQA.propTypes = {
  namespace: PropTypes.string,
  size: PropTypes.number,
  onChange: PropTypes.func,
  seedValue: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.string,
}

export default MediaUploadWithThumbnailDisplayQA;
